import { Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { HttpService } from '../../services/http.service';
import { ToastService } from '../../services/toast.service';
import { environment } from '../../../environments/environment';
import { ScheduledEventDialogComponent } from '../dialogs/scheduled-event-dialog/scheduled-event-dialog.component';

interface ScheduledEvent {
  _id: string;
  name: string;
  description: string;
  eventType: string;
  cronExpression: string;
  timezone: string;
  isActive: boolean;
  lastRun?: Date;
  nextRun?: Date;
  status: string;
  executionCount: number;
  createdAt: Date;
  lastError?: string;
}

@Component({
  selector: 'app-scheduled-events',
  templateUrl: './scheduled-events.component.html',
  styleUrls: ['./scheduled-events.component.scss']
})
export class ScheduledEventsComponent implements OnInit {
  public events: ScheduledEvent[] = [];
  public loading = false;
  public eventTypes: any[] = [];

  constructor(
    private httpService: HttpService,
    private toastService: ToastService,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.loadEvents();
    this.loadEventTypes();
  }

  loadEvents(): void {
    this.loading = true;
    this.httpService.get(environment.API_PATH + '/scheduler/events').subscribe({
      next: (events: ScheduledEvent[]) => {
        this.events = events;
        this.loading = false;
      },
      error: (error) => {
        console.error('Error loading scheduled events:', error);
        this.toastService.error('Failed to load scheduled events');
        this.loading = false;
      }
    });
  }

  loadEventTypes(): void {
    this.httpService.get(environment.API_PATH + '/scheduler/event-types').subscribe({
      next: (types) => {
        this.eventTypes = types;
      },
      error: (error) => {
        console.error('Error loading event types:', error);
      }
    });
  }

  createEvent(): void {
    const dialogRef = this.dialog.open(ScheduledEventDialogComponent, {
      width: '800px',
      disableClose: true,
      data: {
        eventTypes: this.eventTypes,
        mode: 'create'
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.loadEvents();
      }
    });
  }

  editEvent(event: ScheduledEvent): void {
    const dialogRef = this.dialog.open(ScheduledEventDialogComponent, {
      width: '800px',
      disableClose: true,
      data: {
        event,
        eventTypes: this.eventTypes,
        mode: 'edit'
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.loadEvents();
      }
    });
  }

  toggleEvent(event: ScheduledEvent): void {
    const newStatus = !event.isActive;
    this.httpService.patch(environment.API_PATH + `/scheduler/events/${event._id}/toggle`, {
      isActive: newStatus
    }).subscribe({
      next: () => {
        event.isActive = newStatus;
        this.toastService.success(`Event ${newStatus ? 'activated' : 'deactivated'} successfully`);
      },
      error: (error) => {
        console.error('Error toggling event:', error);
        this.toastService.error('Failed to toggle event status');
      }
    });
  }

  runEventNow(event: ScheduledEvent): void {
    if (confirm(`Are you sure you want to run "${event.name}" now?`)) {
      this.httpService.post(environment.API_PATH + `/scheduler/events/${event._id}/run`, {}).subscribe({
        next: () => {
          this.toastService.success('Event execution started');
          this.loadEvents(); // Refresh to show updated status
        },
        error: (error) => {
          console.error('Error running event:', error);
          this.toastService.error('Failed to run event');
        }
      });
    }
  }

  deleteEvent(event: ScheduledEvent): void {
    if (confirm(`Are you sure you want to delete "${event.name}"? This action cannot be undone.`)) {
      this.httpService.delete(environment.API_PATH + `/scheduler/events/${event._id}`).subscribe({
        next: () => {
          this.toastService.success('Event deleted successfully');
          this.loadEvents();
        },
        error: (error) => {
          console.error('Error deleting event:', error);
          this.toastService.error('Failed to delete event');
        }
      });
    }
  }

  getEventTypeLabel(eventType: string): string {
    const type = this.eventTypes.find(t => t.value === eventType);
    return type ? type.label : eventType;
  }

  getStatusColor(status: string): string {
    switch (status) {
      case 'completed': return 'green';
      case 'running': return 'blue';
      case 'failed': return 'red';
      case 'scheduled': return 'orange';
      default: return 'gray';
    }
  }

  formatDate(date: Date | string | undefined): string {
    if (!date) return 'Never';
    return new Date(date).toLocaleString();
  }

  getCronDescription(cronExpression: string): string {
    // Simple cron description - you might want to use a library like cronstrue for better descriptions
    const parts = cronExpression.split(' ');
    if (parts.length === 5) {
      const [minute, hour, day, month, dayOfWeek] = parts;

      if (minute === '0' && hour === '0' && day === '*' && month === '*' && dayOfWeek === '*') {
        return 'Daily at midnight';
      } else if (minute === '0' && hour === '0' && day === '*' && month === '*' && dayOfWeek === '0') {
        return 'Weekly on Sunday at midnight';
      } else if (minute === '0' && hour === '0' && day === '1' && month === '*' && dayOfWeek === '*') {
        return 'Monthly on the 1st at midnight';
      }
    }
    return cronExpression;
  }
}
